import {
  Badge,
  Flex,
  Spin,
} from 'antd'
import React, {
  useMemo,
  useState,
} from 'react'
import useAsync from 'react-use/lib/useAsync'

import { ReportsPageProps } from '@/components/feature/analytics/hooks/use-report-search'
import { Colors } from '@/constants/channel-colors'
import { earningChannels } from '@/constants/earning-channels'
import { EarningType } from '@/constants/earning-type'
import { FeatureKey } from '@/constants/feature-key'
import { readCreatorOverviewChannels } from '@/redux/creator-reports'
import {
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'
import { formatPercentage } from '@/utils/formatter'

import s from '../../creator-reports.module.css'
import ChannelDetailsChart from '../common/channel-details-chart'
import ChartEmptyPlaceholder from '../common/chart-empty-placeholder'
import SectionTitle from '../section-title'

export default function CreatorOverviewChannels({ searchValues }: ReportsPageProps) {
  const { earningType = EarningType.GrossEarnings } = searchValues
  const { creatorOverviewChannels } = useAppSelector(state => state.creatorReports)
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)

  const { startAt, endAt, period, creatorIds } = searchValues

  useAsync(async () => {
    setLoading(true)
    await dispatch(readCreatorOverviewChannels({
      startAt,
      endAt,
      creatorIds,
      period,
      permissionKey: FeatureKey.AnalyticsCreatorOverview,
    }))
    setLoading(false)
  }, [dispatch, startAt, endAt, period, creatorIds])

  const legendTable = useMemo(
    () => {
      const summary = creatorOverviewChannels?.[earningType]?.summary
      return (
        <table className={s.legendTable}>
          <tbody>
            {earningChannels.map(c => {
              const item = summary?.find(i => i.name === c as string)
              return (
                <tr key={c}>
                  <td><Badge color={Colors[c]} text={c} /></td>
                  <td>{formatPercentage()(item?.proportion)}</td>
                  <td>🌟{item?.total}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )
    },
    [creatorOverviewChannels, earningType],
  )

  const { details = [] } = creatorOverviewChannels?.[earningType] || {}

  return (
    <Spin spinning={loading}>
      <Flex vertical className={s.creatorOverviewChannels}>
        <SectionTitle
          title="Earnings Channels"
          tooltip="Track trends and growth of your managed Creators' earning channels"
        />
        {creatorOverviewChannels?.[earningType] === null ?
          <ChartEmptyPlaceholder /> :
          <Flex gap={16}>
            <Flex flex={7} vertical>
              <ChannelDetailsChart details={details} />
            </Flex>
            <Flex flex={3} align='center'>
              {legendTable}
            </Flex>
          </Flex>
        }
      </Flex>
    </Spin>
  )
}
